// Eudonet preset filter catalog for the donations page (OP-5747).
// Each preset is a single filter criterion shown as a checkbox in the "Eudonet"
// group of the "Mes listes" dropdown. The user checks one or more and clicks
// "Créer la liste" to create a custom list whose filter is their combination.
import { DONATION_SOURCE_NONE } from "./donationListFilterParams";
export const DONATION_VIEW_PRESETS = [
    { id: "validated", labelKey: "finance.donations.status.Validated", filterKey: "status", value: "Validated" },
    { id: "pending", labelKey: "finance.donations.status.Pending", filterKey: "status", value: "Recorded" },
    { id: "cancelled", labelKey: "finance.donations.status.Canceled", filterKey: "status", value: "Canceled" },
    { id: "refunded", labelKey: "finance.donations.status.Refunded", filterKey: "status", value: "Refunded" },
    { id: "onetime", labelKey: "finance.donations.type.OneOff", filterKey: "donationType", value: "OneOff" },
    { id: "recurring", labelKey: "finance.donations.type.Regular", filterKey: "donationType", value: "Regular" },
    { id: "card", labelKey: "finance.donations.paymentMethod.Card", filterKey: "paymentMethod", value: "Card" },
    {
        id: "directdebit",
        labelKey: "finance.donations.paymentMethod.DirectDebit",
        filterKey: "paymentMethod",
        value: "DirectDebit",
    },
    { id: "cheque", labelKey: "finance.donations.paymentMethod.Cheque", filterKey: "paymentMethod", value: "Cheque" },
    {
        id: "banktransfer",
        labelKey: "finance.donations.paymentMethod.BankTransfer",
        filterKey: "paymentMethod",
        value: "BankTransfer",
    },
];
/**
 * Maps a table filter key to the i18n sub-namespace under `finance.donations`, so any filter
 * value (including ones outside the Eudonet preset catalog, e.g. Cash/Promesse) can be labelled
 * via `finance.donations.{group}.{value}`.
 */
export const FILTER_KEY_I18N_GROUP = {
    status: "status",
    donationType: "type",
    paymentMethod: "paymentMethod",
    donationSource: "donationSource",
    campaignId: "campaignId",
};
const PRESET_BY_ID = new Map(DONATION_VIEW_PRESETS.map((p) => [p.id, p]));
/** Returns the i18n label key for a preset id, or undefined if the id is unknown/stale. */
export function presetLabelKey(id) {
    return PRESET_BY_ID.get(id)?.labelKey;
}
const PRESET_BY_FILTER_VALUE = new Map(DONATION_VIEW_PRESETS.map((p) => [`${p.filterKey}:${p.value}`, p]));
/**
 * i18n label key for a (filterKey, value) pair. Reuses the Eudonet preset's label when one
 * matches — so a list built from the "Pending" preset (status=Recorded) is named via the same
 * `status.Pending` key the preset tab/checkbox uses, instead of the raw `status.Recorded`.
 * Values outside the preset catalog (e.g. Cash, Promesse) fall back to the value-derived
 * `finance.donations.{group}.{value}` key.
 */
export function filterValueLabelKey(filterKey, value) {
    if (filterKey === "donationSource" && value === DONATION_SOURCE_NONE) {
        return "finance.donations.filter.noSource";
    }
    const preset = PRESET_BY_FILTER_VALUE.get(`${filterKey}:${value}`);
    if (preset)
        return preset.labelKey;
    const group = FILTER_KEY_I18N_GROUP[filterKey] ?? filterKey;
    return `finance.donations.${group}.${value}`;
}
/**
 * Builds a table query snapshot from a set of preset ids, grouping by filter key
 * (so two statuses become `status in [a, b]`, while a status + a method AND together).
 * Unknown ids are ignored.
 */
export function buildPresetQuerySnapshot(presetIds) {
    const filters = {};
    for (const id of presetIds) {
        const preset = PRESET_BY_ID.get(id);
        if (!preset)
            continue;
        const existing = filters[preset.filterKey];
        if (existing) {
            if (!existing.values.includes(preset.value))
                existing.values.push(preset.value);
        }
        else {
            filters[preset.filterKey] = { type: "in", values: [preset.value] };
        }
    }
    return { filters };
}
